import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Shield, Zap, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services, stats, howItWorks, faqs, testimonials } from '../data/mock';
import * as Icons from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>PULVISTAR - Nettoyage professionnel par drone | Toitures, panneaux solaires, façades</title>
        <meta name="description" content="PULVISTAR, expert du nettoyage par drone en France. Toitures, panneaux solaires, éoliennes, façades, monuments historiques et copropriétés. Sans échafaudage, rapide et économique. Devis gratuit en 24h." />
        <link rel="canonical" href="https://pulvistar.fr/" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-white z-0 animate-gradient"></div>
        
        {/* Animated Blobs */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full blur-3xl opacity-20 animate-float animate-blob-morph"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full blur-3xl opacity-20 animate-float delay-300 animate-blob-morph"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-br from-cyan-300 to-blue-400 rounded-full blur-3xl opacity-10 animate-float delay-500"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-40 left-20 w-2 h-2 bg-cyan-500 rounded-full animate-bounce-subtle"></div>
        <div className="absolute bottom-40 right-32 w-3 h-3 bg-blue-500 rounded-full animate-bounce-subtle delay-200"></div>
        <div className="absolute top-60 right-48 w-2 h-2 bg-cyan-400 rounded-full animate-bounce-subtle delay-400"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slideInLeft">
              <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-700 hover:from-cyan-200 hover:to-blue-200 border-0 animate-pulse-glow shine-effect" style={{ fontFamily: '"Inter", sans-serif' }}>
                <span className="relative z-10">+250 clients nous font confiance</span>
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight animate-fadeInUp" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
                <span className="gradient-text-animated">Nettoyage professionnel</span><br />
                par drone
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed animate-fadeInUp delay-200" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                Performance, sécurité, rentabilité. Intervention rapide sans échafaudage pour toitures, panneaux solaires, éoliennes, façades, bâtiments industrielles & commerciaux.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp delay-300">
                <Button size="lg" className="gap-2 bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 transition-bounce hover:scale-110 hover:shadow-2xl group shine-effect" onClick={() => window.location.href = '/devis'}>
                  <span className="relative z-10">Demander un devis</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-smooth" />
                </Button>
                <Button size="lg" variant="outline" className="gap-2 transition-bounce hover:scale-110 hover:shadow-xl border-2 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50" onClick={() => window.location.href = 'tel:+33184180342'}>
                  Appeler maintenant
                </Button>
              </div>
            </div>
            <div className="relative animate-slideInRight">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-blue-500 to-blue-600 rounded-2xl transform rotate-3 opacity-20 animate-pulse"></div>
              <div className="absolute -inset-4 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl blur-2xl opacity-20 animate-pulse delay-300"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_eco-drone-1/artifacts/83t2zgfh_image%20final.png"
                alt="Drone PULVISTAR nettoyage professionnel"
                className="relative z-10 rounded-2xl shadow-2xl w-full h-auto transition-smooth hover:scale-105 hover:shadow-cyan-500/50 hover:shadow-2xl shine-effect"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-cyan-500 to-transparent animate-gradient"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className={`text-center group animate-scaleIn delay-${index * 100}`}>
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-cyan-400 rounded-full blur-xl opacity-0 group-hover:opacity-50 transition-smooth"></div>
                  <div className="text-4xl lg:text-5xl font-bold mb-2 gradient-text counter-animate relative z-10" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {stat.value}
                  </div>
                </div>
                <div className="text-sm lg:text-base text-blue-200 transition-smooth group-hover:text-white group-hover:scale-110" style={{ fontFamily: '"Inter", sans-serif' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-32 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-cyan-100 rounded-full blur-3xl opacity-30 animate-float"></div>
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-blue-100 rounded-full blur-3xl opacity-30 animate-float delay-400"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Pourquoi choisir <span className="gradient-text">PULVISTAR</span> ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fadeInUp delay-100" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Une expertise technique au service de vos installations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Sécurité maximale',
                description: 'Réduction des risques humains, intervention sans accès physique dangereux. Assuré et conforme aux réglementations.',
                gradient: 'from-cyan-500 to-blue-500'
              },
              {
                icon: Zap,
                title: 'Intervention rapide',
                description: 'Déploiement en 48h, sans échafaudage ni nacelle. Gain de temps et réduction des nuisances.',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: Target,
                title: 'Précision technique',
                description: 'Pulvérisation calibrée selon le support, respect des matériaux, protocoles éprouvés.',
                gradient: 'from-cyan-400 to-blue-600'
              },
              {
                icon: TrendingUp,
                title: 'Efficacité opérationnelle',
                description: 'Jusqu\'à 30% moins cher que les méthodes traditionnelles, optimisation des ressources.',
                gradient: 'from-blue-600 to-cyan-400'
              },
              {
                icon: CheckCircle2,
                title: 'Qualité garantie',
                description: 'Contrôle qualité systématique, photos avant/après, recommandations personnalisées.',
                gradient: 'from-cyan-500 to-blue-500'
              },
              {
                icon: Shield,
                title: 'Approche responsable',
                description: 'Optimisation des ressources, limitation des moyens lourds, respect de l\'environnement.',
                gradient: 'from-blue-500 to-cyan-600'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className={`p-6 border-gray-200 hover:border-cyan-300 hover:shadow-2xl transition-bounce hover:-translate-y-3 hover-glow group animate-scaleIn delay-${index * 100} shine-effect cursor-pointer`}>
                  <div className={`w-14 h-14 bg-gradient-to-br ${benefit.gradient} rounded-lg flex items-center justify-center mb-4 transition-bounce group-hover:scale-125 group-hover:rotate-12 relative`}>
                    <div className="absolute inset-0 bg-white rounded-lg opacity-0 group-hover:opacity-20 animate-pulse"></div>
                    <IconComponent className="w-7 h-7 text-white relative z-10" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-900 transition-smooth" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-smooth" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                    {benefit.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="animate-slideInLeft">
                <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
                  Comment ça marche ?
                </h2>
                <p className="text-xl text-gray-600" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  Un processus éprouvé en 4 étapes pour garantir un nettoyage professionnel et sécurisé de vos installations industrielles
                </p>
              </div>
              <div className="relative animate-slideInRight">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl transform -rotate-3 opacity-10 animate-pulse"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_eco-drone-1/artifacts/23opqm9h_Gemini_Generated_Image_138kn7138kn7138k.png"
                  alt="Installation industrielle"
                  className="relative z-10 rounded-2xl shadow-xl w-full h-80 object-cover transition-smooth hover:scale-105"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className={`relative animate-fadeInUp delay-${index * 100}`}>
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 h-full transition-smooth hover:shadow-xl hover:-translate-y-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4 animate-pulse-glow" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      {step.step}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      {step.title}
                    </h3>
                    <p className="text-gray-600" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      {step.description}
                    </p>
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10 animate-float">
                      <ArrowRight className="w-8 h-8 text-cyan-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-32 relative">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #00D4FF 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 animate-fadeInUp" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Nos <span className="gradient-text">solutions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fadeInUp delay-100" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Des solutions personnalisées et adaptées à chaque besoin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = Icons[service.icon];
              return (
                <Card key={service.id} className={`overflow-hidden border-gray-200 hover:border-cyan-300 hover:shadow-2xl transition-bounce hover:-translate-y-4 group animate-scaleIn delay-${index * 100} relative shine-effect`}>
                  {/* Hover overlay effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 transition-smooth-slow z-10 pointer-events-none"></div>
                  
                  <div className="relative h-48 overflow-hidden bg-gray-200">
                    <img
                      src={service.image}
                      alt={service.title}
                      className={`w-full h-full object-cover group-hover:scale-125 group-hover:rotate-2 transition-smooth-slow ${service.id === 'toitures' ? 'brightness-125' : ''} ${service.id === 'monuments-historiques' ? 'brightness-150' : ''}`}
                      loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/40 transition-smooth"></div>
                    <div className="absolute bottom-4 left-4 transition-bounce group-hover:scale-125 group-hover:-translate-y-2">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center animate-pulse-glow group-hover:bg-gradient-to-br group-hover:from-cyan-400 group-hover:to-blue-500 transition-smooth">
                        <IconComponent className="w-6 h-6 text-blue-900 group-hover:text-white transition-smooth" />
                      </div>
                    </div>
                  </div>
                  <div className="p-6 relative z-20">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-900 transition-smooth" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4 group-hover:text-gray-700 transition-smooth" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      {service.description}
                    </p>
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center gap-2 text-blue-900 font-medium hover:gap-4 transition-smooth group/link"
                      style={{ fontFamily: '"Inter", sans-serif' }}
                    >
                      En savoir plus <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-smooth" />
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12 animate-fadeInUp delay-600">
            <Button size="lg" variant="outline" className="transition-bounce hover:scale-110 hover:shadow-xl border-2 hover:border-cyan-500 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 group" onClick={() => window.location.href = '/services'}>
              <span>Voir tous les services</span>
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-smooth" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Ils nous font confiance
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-white">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-white/90 mb-6 italic" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-semibold" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-white/70" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {testimonial.role}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Questions fréquentes
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-900 py-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="text-center mt-12">
            <Button size="lg" variant="outline" onClick={() => window.location.href = '/faq'}>
              Voir toutes les questions
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Prêt à optimiser vos installations ?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
            Demandez votre devis gratuit dès maintenant. Réponse sous 24h.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => window.location.href = '/devis'}>
              Obtenir un devis <ArrowRight className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="gap-2" onClick={() => window.location.href = '/contact'}>
              Nous contacter
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
